/** Monument icon bodies — the SVG shapes for each landmark, painted with
 * the frame's --st* / --gd* variables. Each export returns the inner
 * shapes only; MonumentFrame wraps them in a coloured <svg>. viewBox
 * 160×120 for all.
 *
 * Style: two-tone flat-clay with fluting/tiers, soft highlights and
 * shadows, a small gold accent per landmark. Recognizable silhouette
 * over literal accuracy.
 */
import type { ReactElement } from "react";

const COLS = [29, 49, 69, 91, 111, 131];
const DENTILS = Array.from({ length: 20 }, (_, i) => 20 + i * 6);

/** Rounded-top (arched) opening as an SVG path string. */
function archPath(x: number, y: number, w: number, h: number): string {
  const r = w / 2;
  return `M${x} ${y + h} L${x} ${y + r} Q${x} ${y} ${x + r} ${y} Q${x + w} ${y} ${x + w} ${y + r} L${x + w} ${y + h} Z`;
}

// --- Shared motifs used across the set --------------------------------------
/** Triangular roof/spire on top of a block. */
function cone(cx: number, baseY: number, w: number, h: number, gold = false): ReactElement {
  return (
    <>
      <path d={`M${cx - w / 2} ${baseY} L${cx} ${baseY - h} L${cx + w / 2} ${baseY} Z`} fill={gold ? "var(--gd)" : "var(--st)"} />
      <path d={`M${cx - w / 2} ${baseY} L${cx} ${baseY - h} L${cx} ${baseY} Z`} fill="var(--st-hi)" opacity="0.28" />
    </>
  );
}
/** Rounded dome (quadratic, bulges up) on a baseline. */
function dome(cx: number, baseY: number, rx: number, ry: number, gold = false): ReactElement {
  return (
    <>
      <path d={`M${cx - rx} ${baseY} Q${cx} ${baseY - ry * 1.9} ${cx + rx} ${baseY} Z`} fill={gold ? "var(--gd)" : "var(--st)"} />
      <path
        d={`M${cx - rx * 0.55} ${baseY - ry * 0.5} Q${cx - rx * 0.1} ${baseY - ry * 1.2} ${cx + rx * 0.25} ${baseY - ry * 0.9}`}
        fill="none" stroke={gold ? "var(--gd-hi)" : "var(--st-hi)"} strokeWidth="1" opacity="0.5"
      />
    </>
  );
}
/** Small gold finial + ball on a peak. */
function finial(cx: number, topY: number): ReactElement {
  return (
    <>
      <rect x={cx - 0.5} y={topY - 5} width="1" height="5" fill="var(--gd)" />
      <circle cx={cx} cy={topY - 6} r="1.4" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Brandenburger Tor — Berlin
// ---------------------------------------------------------------------------
export function brandenburgerTor(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="66" ry="5" fill="var(--st-dp)" opacity="0.22" />
      <rect x="15" y="105" width="130" height="7" rx="2.5" fill="var(--st)" />
      <rect x="15" y="105" width="130" height="1.6" rx="0.8" fill="var(--st-hi)" />
      <rect x="15" y="110" width="130" height="2" fill="var(--st-dp)" opacity="0.4" />
      <rect x="21" y="99" width="118" height="6" rx="2" fill="var(--st)" />
      <rect x="21" y="99" width="118" height="1.4" rx="0.7" fill="var(--st-hi)" />
      <rect x="26" y="94" width="108" height="5.2" rx="1.5" fill="var(--st)" />
      <rect x="26" y="94" width="108" height="1.2" rx="0.6" fill="var(--st-hi)" />
      <rect x="26" y="55" width="108" height="39" rx="1.5" fill="var(--st-dp)" />
      {COLS.map((cx) => (
        <g key={cx}>
          <rect x={cx - 5.5} y="55" width="11" height="39" fill="var(--st)" />
          <rect x={cx - 2.4} y="57" width="0.9" height="35" fill="var(--st-sh)" opacity="0.45" />
          <rect x={cx + 0.4} y="57" width="0.9" height="35" fill="var(--st-sh)" opacity="0.45" />
          <rect x={cx - 5.5} y="56" width="1.5" height="37" fill="var(--st-hi)" />
          <rect x={cx + 4} y="56" width="1.5" height="37" fill="var(--st-dp)" opacity="0.5" />
          <rect x={cx - 6.8} y="52" width="13.6" height="3" rx="0.6" fill="var(--st)" />
          <rect x={cx - 6.8} y="52" width="13.6" height="1" fill="var(--st-hi)" />
          <rect x={cx - 6.8} y="91" width="13.6" height="3.4" rx="0.6" fill="var(--st)" />
          <rect x={cx - 6.8} y="93.6" width="13.6" height="0.8" fill="var(--st-dp)" opacity="0.5" />
        </g>
      ))}
      <rect x="17" y="49" width="126" height="5.4" fill="var(--st)" />
      <rect x="17" y="52.8" width="126" height="1.6" fill="var(--st-dp)" opacity="0.32" />
      <rect x="17" y="44" width="126" height="5.2" fill="var(--st)" />
      {COLS.map((cx) => (
        <g key={cx}>
          <rect x={cx - 2} y="44.4" width="4" height="4.6" rx="0.4" fill="var(--st-sh)" opacity="0.6" />
          <rect x={cx - 0.5} y="44.8" width="1" height="3.8" fill="var(--st-dp)" opacity="0.5" />
        </g>
      ))}
      <rect x="14" y="39" width="132" height="5" rx="1.5" fill="var(--st)" />
      <rect x="14" y="39" width="132" height="1.5" rx="0.8" fill="var(--st-hi)" />
      {DENTILS.map((dx) => (
        <rect key={dx} x={dx} y="42.6" width="3" height="1.6" fill="var(--st-sh)" opacity="0.5" />
      ))}
      <rect x="32" y="22" width="96" height="17" rx="2" fill="var(--st)" />
      <rect x="32" y="22" width="96" height="1.6" rx="0.8" fill="var(--st-hi)" />
      <rect x="32" y="37" width="96" height="2" fill="var(--st-dp)" opacity="0.32" />
      <rect x="40" y="26" width="17" height="8" rx="1" fill="var(--st-sh)" />
      <rect x="40" y="26" width="17" height="1.4" fill="var(--st-dp)" opacity="0.55" />
      <rect x="103" y="26" width="17" height="8" rx="1" fill="var(--st-sh)" />
      <rect x="103" y="26" width="17" height="1.4" fill="var(--st-dp)" opacity="0.55" />
      <rect x="63" y="25" width="34" height="10" rx="1" fill="var(--st-sh)" />
      <rect x="63" y="25" width="34" height="1.5" fill="var(--st-dp)" opacity="0.55" />
      <rect x="63" y="33.5" width="34" height="1.3" fill="var(--st-hi)" opacity="0.7" />
      <rect x="63" y="19" width="34" height="3.5" rx="1" fill="var(--gd-sh)" />
      <rect x="63" y="19" width="34" height="1" fill="var(--gd-hi)" />
      <circle cx="90.5" cy="17" r="4" fill="var(--gd)" />
      <circle cx="90.5" cy="17" r="1.2" fill="var(--gd-dp)" />
      <g stroke="var(--gd-sh)" strokeWidth="0.7">
        <line x1="90.5" y1="13.2" x2="90.5" y2="20.8" />
        <line x1="86.7" y1="17" x2="94.3" y2="17" />
        <line x1="87.8" y1="14.3" x2="93.2" y2="19.7" />
        <line x1="87.8" y1="19.7" x2="93.2" y2="14.3" />
      </g>
      <path d="M65 18.5 Q63 10 72 9 L82 9 Q87 9 87 18.5 Z" fill="var(--gd)" />
      <path d="M65 18.5 Q64 12 70 11 L80 11 Q84 11 84 18.5 Z" fill="var(--gd)" opacity="0.92" />
      <path d="M66 11 Q72 9.4 82 9.6" fill="none" stroke="var(--gd-hi)" strokeWidth="1.1" strokeLinecap="round" />
      <g fill="var(--gd)">
        {[66, 70, 75, 80].map((lx) => (
          <rect key={lx} x={lx} y="14" width="1.7" height="5" rx="0.6" />
        ))}
      </g>
      <path d="M67 12 Q59 10 57 5 Q60 4 62 8 Q64 10 69 13 Z" fill="var(--gd)" />
      <path d="M70 11 Q63 9 62 5 Q65 4 66 8 Q68 10 71 12 Z" fill="var(--gd)" />
      <path d="M73 11 Q68 9.5 68 6 Q70 5 71 8.5 Q72 10 74.5 12 Z" fill="var(--gd)" />
      <path d="M85 10 Q89 12 87.5 17" fill="none" stroke="var(--gd)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M86 10 Q81 7.5 80.5 3.5 Q85 5.5 88 9.5 Z" fill="var(--gd-sh)" opacity="0.85" />
      <path d="M90 10 Q95 7.5 95.5 4 Q91 6 89 9.5 Z" fill="var(--gd-sh)" opacity="0.7" />
      <path d="M86 18.5 L84.5 8 Q84.5 6 88 6 Q91.5 6 91.5 8 L90 18.5 Z" fill="var(--gd)" />
      <circle cx="88" cy="5" r="2.1" fill="var(--gd)" />
      <rect x="91.4" y="1.5" width="1.4" height="7.5" rx="0.7" fill="var(--gd)" />
      <circle cx="92.1" cy="1.4" r="1.9" fill="var(--gd)" />
      <circle cx="92.1" cy="1.4" r="0.8" fill="var(--gd-dp)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Eiffel Tower — Paris
// ---------------------------------------------------------------------------
export function eiffelTower(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="44" ry="4.5" fill="var(--st-dp)" opacity="0.2" />
      {/* legs */}
      <path d="M40 110 L56 110 L63 66 L57 66 Z" fill="var(--st)" />
      <path d="M120 110 L104 110 L97 66 L103 66 Z" fill="var(--st)" />
      <path d="M40 110 L46 110 L60 66 L57 66 Z" fill="var(--st-hi)" opacity="0.55" />
      {/* grand base arch */}
      <path d="M61 66 Q80 92 99 66" fill="none" stroke="var(--st)" strokeWidth="3.4" strokeLinecap="round" />
      {/* platform 1 */}
      <rect x="50" y="61" width="60" height="6" rx="1.5" fill="var(--st)" />
      <rect x="50" y="61" width="60" height="1.4" fill="var(--st-hi)" />
      {/* mid section */}
      <path d="M56 61 L70 42 L90 42 L104 61 Z" fill="var(--st)" />
      <g stroke="var(--st-sh)" strokeWidth="0.8" opacity="0.5" fill="none">
        <path d="M62 59 L86 44 M78 59 L98 44 M84 59 L64 44" />
      </g>
      {/* platform 2 */}
      <rect x="66" y="38" width="28" height="4.5" rx="1" fill="var(--st)" />
      <rect x="66" y="38" width="28" height="1.2" fill="var(--st-hi)" />
      {/* upper tower + spire */}
      <path d="M70 38 L77 20 L83 20 L90 38 Z" fill="var(--st)" />
      <path d="M76 20 L78.5 12 L81.5 12 L84 20 Z" fill="var(--st)" />
      <g stroke="var(--st-sh)" strokeWidth="0.7" opacity="0.5" fill="none">
        <path d="M73 36 L86 22 M87 36 L74 22" />
      </g>
      {/* beacon + flag (gold) */}
      <rect x="79" y="5" width="2" height="8" rx="1" fill="var(--gd)" />
      <circle cx="80" cy="4.5" r="2" fill="var(--gd)" />
      <circle cx="80" cy="4.5" r="0.8" fill="var(--gd-hi)" />
      <path d="M81 5.5 L86 7 L81 8.5 Z" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Colosseum — Rome
// ---------------------------------------------------------------------------
export function colosseum(): ReactElement {
  const row1 = Array.from({ length: 9 }, (_, i) => 30 + i * 12);
  const row2 = Array.from({ length: 9 }, (_, i) => 31 + i * 11.6);
  return (
    <>
      <ellipse cx="80" cy="112" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      {/* facade */}
      <path
        d="M24 98 L24 60 Q24 44 42 41 Q80 33 118 41 Q136 44 136 60 L136 98 Z"
        fill="var(--st)"
      />
      <path d="M42 41 Q80 33 118 41" fill="none" stroke="var(--st-hi)" strokeWidth="1.6" strokeLinecap="round" />
      {/* base ledge */}
      <rect x="22" y="94" width="116" height="6" rx="2" fill="var(--st)" />
      <rect x="22" y="94" width="116" height="1.3" fill="var(--st-hi)" />
      {/* tier dividers */}
      <rect x="26" y="64" width="108" height="1.6" fill="var(--st-sh)" opacity="0.6" />
      <rect x="28" y="49" width="104" height="1.4" fill="var(--st-sh)" opacity="0.6" />
      {/* arch rows */}
      {row1.map((x) => (
        <path key={x} d={archPath(x, 68, 8, 22)} fill="var(--st-dp)" />
      ))}
      {row2.map((x) => (
        <path key={x} d={archPath(x, 52, 6, 11)} fill="var(--st-dp)" opacity="0.9" />
      ))}
      {/* ruined bite, top-left */}
      <path d="M40 41 L48 41 L44 35 Z" fill="var(--st-dp)" opacity="0.5" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Leaning Tower of Pisa
// ---------------------------------------------------------------------------
export function leaningTowerPisa(): ReactElement {
  const drums = [86, 75, 64, 53, 42, 31];
  return (
    <>
      <ellipse cx="88" cy="114" rx="22" ry="4" fill="var(--st-dp)" opacity="0.2" />
      <g transform="rotate(7 80 106)">
        {/* base */}
        <rect x="68" y="97" width="24" height="10" rx="1.5" fill="var(--st)" />
        <rect x="68" y="97" width="24" height="1.4" fill="var(--st-hi)" />
        <path d={archPath(76, 100, 8, 7)} fill="var(--st-dp)" />
        {/* arcade drums */}
        {drums.map((y) => (
          <g key={y}>
            <rect x="69" y={y} width="22" height="10" rx="1" fill="var(--st)" />
            <rect x="69" y={y} width="1.4" height="10" fill="var(--st-hi)" />
            <rect x="89.6" y={y} width="1.4" height="10" fill="var(--st-dp)" opacity="0.5" />
            {[72, 76.5, 81, 85.5].map((sx) => (
              <rect key={sx} x={sx} y={y + 2.4} width="1.6" height="6" rx="0.6" fill="var(--st-dp)" opacity="0.55" />
            ))}
          </g>
        ))}
        {/* belfry */}
        <rect x="71" y="21" width="18" height="11" rx="1.5" fill="var(--st)" />
        <rect x="71" y="21" width="18" height="1.3" fill="var(--st-hi)" />
        <path d={archPath(76, 24, 8, 8)} fill="var(--st-dp)" />
        {/* bell (gold) */}
        <path d="M78 26 Q78 24 80 24 Q82 24 82 26 L82.6 29 L77.4 29 Z" fill="var(--gd)" />
        <circle cx="80" cy="29.6" r="0.9" fill="var(--gd-dp)" />
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// Big Ben (Elizabeth Tower) — London
// ---------------------------------------------------------------------------
export function bigBen(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="26" ry="4" fill="var(--st-dp)" opacity="0.2" />
      <rect x="66" y="104" width="28" height="6" rx="1.5" fill="var(--st)" />
      <rect x="66" y="104" width="28" height="1.3" fill="var(--st-hi)" />
      {/* shaft */}
      <rect x="70" y="44" width="20" height="62" fill="var(--st)" />
      <rect x="70" y="45" width="1.6" height="59" fill="var(--st-hi)" />
      <rect x="88.4" y="45" width="1.6" height="59" fill="var(--st-dp)" opacity="0.5" />
      {[62, 74, 86, 98].map((y) => (
        <path key={y} d={archPath(76, y, 8, 6)} fill="var(--st-dp)" opacity="0.42" />
      ))}
      {/* belfry */}
      <rect x="65" y="34" width="30" height="12" rx="1.5" fill="var(--st)" />
      <rect x="65" y="34" width="30" height="1.4" fill="var(--st-hi)" />
      {[68, 74.5, 81, 87.5].map((x) => (
        <rect key={x} x={x} y="37" width="3.5" height="7" rx="1" fill="var(--st-dp)" opacity="0.5" />
      ))}
      {/* clock */}
      <circle cx="80" cy="52" r="6.6" fill="var(--gd)" />
      <circle cx="80" cy="52" r="5" fill="var(--st-hi)" />
      <g stroke="var(--gd-dp)" strokeWidth="0.9" strokeLinecap="round">
        <line x1="80" y1="52" x2="80" y2="48" />
        <line x1="80" y1="52" x2="83" y2="53" />
      </g>
      <circle cx="80" cy="52" r="0.9" fill="var(--gd-dp)" />
      {/* spire */}
      <path d="M65 34 L80 12 L95 34 Z" fill="var(--st)" />
      <path d="M65 34 L80 12 L80 34 Z" fill="var(--st-hi)" opacity="0.35" />
      <rect x="79" y="5" width="2" height="8" rx="1" fill="var(--gd)" />
      <circle cx="80" cy="5" r="1.9" fill="var(--gd)" />
      <circle cx="80" cy="5" r="0.8" fill="var(--gd-hi)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Kölner Dom — Cologne
// ---------------------------------------------------------------------------
function gothicTower(cx: number) {
  return (
    <g>
      <rect x={cx - 11} y="46" width="22" height="58" fill="var(--st)" />
      <rect x={cx - 11} y="46" width="1.6" height="58" fill="var(--st-hi)" />
      <rect x={cx + 9.4} y="46" width="1.6" height="58" fill="var(--st-dp)" opacity="0.5" />
      {[52, 66, 80, 94].map((y) => (
        <path key={y} d={archPath(cx - 4, y, 8, 9)} fill="var(--st-dp)" opacity="0.5" />
      ))}
      <path d={`M${cx - 11} 46 L${cx} 8 L${cx + 11} 46 Z`} fill="var(--st)" />
      <path d={`M${cx - 11} 46 L${cx} 8 L${cx} 46 Z`} fill="var(--st-hi)" opacity="0.3" />
      <g stroke="var(--st-dp)" strokeWidth="0.7" opacity="0.5">
        <line x1={cx - 6} y1="40" x2={cx + 6} y2="40" />
        <line x1={cx - 4} y1="30" x2={cx + 4} y2="30" />
        <line x1={cx - 2.5} y1="20" x2={cx + 2.5} y2="20" />
      </g>
      <rect x={cx - 0.6} y="1" width="1.2" height="8" fill="var(--gd)" />
      <rect x={cx - 2} y="3.5" width="4" height="1.2" fill="var(--gd)" />
    </g>
  );
}

export function koelnerDom(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="24" y="104" width="112" height="6" rx="2" fill="var(--st)" />
      <rect x="24" y="104" width="112" height="1.3" fill="var(--st-hi)" />
      <rect x="56" y="60" width="48" height="44" fill="var(--st)" />
      <path d="M56 60 L80 40 L104 60 Z" fill="var(--st)" />
      <path d="M56 60 L80 40 L80 60 Z" fill="var(--st-hi)" opacity="0.3" />
      <circle cx="80" cy="66" r="6" fill="var(--st-dp)" opacity="0.55" />
      <circle cx="80" cy="66" r="6" fill="none" stroke="var(--st-hi)" strokeWidth="1" />
      <path d={archPath(72, 84, 16, 20)} fill="var(--st-dp)" />
      <rect x="79.4" y="34" width="1.2" height="7" fill="var(--gd)" />
      <rect x="78" y="36.5" width="4" height="1.2" fill="var(--gd)" />
      {gothicTower(35)}
      {gothicTower(125)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Arc de Triomphe — Paris
// ---------------------------------------------------------------------------
export function arcDeTriomphe(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="56" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="30" y="104" width="100" height="6" rx="2" fill="var(--st)" />
      <rect x="30" y="104" width="100" height="1.3" fill="var(--st-hi)" />
      <rect x="36" y="30" width="88" height="74" fill="var(--st)" />
      <rect x="36" y="30" width="2" height="74" fill="var(--st-hi)" />
      <rect x="122" y="30" width="2" height="74" fill="var(--st-dp)" opacity="0.5" />
      <rect x="33" y="22" width="94" height="9" rx="1.5" fill="var(--st)" />
      <rect x="33" y="22" width="94" height="1.5" fill="var(--st-hi)" />
      <rect x="34" y="31" width="92" height="2.5" fill="var(--st-dp)" opacity="0.35" />
      <path d={archPath(62, 52, 36, 52)} fill="var(--st-dp)" />
      <path d={archPath(62, 52, 36, 52)} fill="none" stroke="var(--st-hi)" strokeWidth="1" opacity="0.5" />
      <rect x="42" y="54" width="14" height="26" rx="1" fill="var(--st-sh)" />
      <rect x="42" y="54" width="14" height="1.3" fill="var(--st-dp)" opacity="0.5" />
      <rect x="104" y="54" width="14" height="26" rx="1" fill="var(--st-sh)" />
      <rect x="104" y="54" width="14" height="1.3" fill="var(--st-dp)" opacity="0.5" />
      <rect x="60" y="24" width="40" height="5" rx="1" fill="var(--st-sh)" />
      <circle cx="80" cy="44" r="4" fill="none" stroke="var(--gd)" strokeWidth="2" />
      <circle cx="80" cy="44" r="1.4" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tower Bridge — London
// ---------------------------------------------------------------------------
function bridgeTower(x: number) {
  return (
    <g>
      <rect x={x} y="36" width="22" height="62" fill="var(--st)" />
      <rect x={x} y="37" width="1.6" height="60" fill="var(--st-hi)" />
      <rect x={x + 20.4} y="37" width="1.6" height="60" fill="var(--st-dp)" opacity="0.5" />
      {[44, 60, 76].map((y) => (
        <path key={y} d={archPath(x + 7, y, 8, 9)} fill="var(--st-dp)" opacity="0.5" />
      ))}
      <path d={`M${x - 1} 36 L${x + 11} 14 L${x + 23} 36 Z`} fill="var(--st)" />
      <path d={`M${x - 1} 36 L${x + 11} 14 L${x + 11} 36 Z`} fill="var(--st-hi)" opacity="0.3" />
      <rect x={x - 2} y="30" width="4" height="7" fill="var(--st)" />
      <rect x={x + 20} y="30" width="4" height="7" fill="var(--st)" />
      <rect x={x + 10.4} y="9" width="1.2" height="6" fill="var(--gd)" />
      <circle cx={x + 11} cy="9" r="1.5" fill="var(--gd)" />
    </g>
  );
}

export function towerBridge(): ReactElement {
  return (
    <>
      <rect x="8" y="98" width="144" height="8" rx="2" fill="var(--st-dp)" opacity="0.3" />
      <rect x="10" y="86" width="30" height="12" fill="var(--st)" />
      <rect x="120" y="86" width="30" height="12" fill="var(--st)" />
      {bridgeTower(38)}
      {bridgeTower(100)}
      <rect x="60" y="40" width="40" height="5" fill="var(--st)" />
      <rect x="60" y="40" width="40" height="1.2" fill="var(--st-hi)" />
      <rect x="60" y="47" width="40" height="4" fill="var(--st)" />
      <path d="M40 52 Q50 70 60 62 M120 52 Q110 70 100 62" fill="none" stroke="var(--gd)" strokeWidth="1.6" />
      <rect x="60" y="80" width="40" height="5" fill="var(--st)" />
      <rect x="60" y="80" width="40" height="1.2" fill="var(--st-hi)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// St. Basil's Cathedral — Moscow
// ---------------------------------------------------------------------------
function onionDome(cx: number, baseY: number, r: number, gold: boolean) {
  const topY = baseY - 7 - r * 2;
  return (
    <g>
      <rect x={cx - r * 0.75} y={baseY - 8} width={r * 1.5} height="8" fill="var(--st)" />
      <path
        d={`M${cx - r} ${baseY - 7} Q${cx - r * 1.2} ${baseY - 7 - r * 1.3} ${cx} ${topY} Q${cx + r * 1.2} ${baseY - 7 - r * 1.3} ${cx + r} ${baseY - 7} Z`}
        fill={gold ? "var(--gd)" : "var(--st)"}
      />
      <path
        d={`M${cx - r * 0.5} ${baseY - 9} Q${cx} ${baseY - 7 - r} ${cx + r * 0.5} ${baseY - 9}`}
        fill="none"
        stroke={gold ? "var(--gd-dp)" : "var(--st-sh)"}
        strokeWidth="0.7"
        opacity="0.6"
      />
      <rect x={cx - 0.5} y={topY - 6} width="1" height="6" fill="var(--gd)" />
      <rect x={cx - 1.6} y={topY - 4} width="3.2" height="1" fill="var(--gd)" />
    </g>
  );
}

export function stBasils(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="52" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="30" y="104" width="100" height="6" rx="2" fill="var(--st)" />
      <rect x="40" y="70" width="18" height="34" fill="var(--st)" />
      <rect x="102" y="70" width="18" height="34" fill="var(--st)" />
      <rect x="62" y="56" width="36" height="48" fill="var(--st)" />
      <rect x="62" y="56" width="2" height="48" fill="var(--st-hi)" />
      {[45, 107].map((x) => (
        <path key={x} d={archPath(x, 86, 10, 18)} fill="var(--st-dp)" opacity="0.5" />
      ))}
      <path d={archPath(72, 80, 16, 24)} fill="var(--st-dp)" />
      {onionDome(80, 56, 12, true)}
      {onionDome(49, 70, 8, false)}
      {onionDome(111, 70, 8, false)}
      {onionDome(35, 104, 6.5, false)}
      {onionDome(125, 104, 6.5, false)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Atomium — Brussels
// ---------------------------------------------------------------------------
export function atomium(): ReactElement {
  const nodes: number[][] = [
    [80, 14], [80, 106], [44, 60], [116, 60],
    [58, 37], [102, 37], [58, 83], [102, 83], [80, 60],
  ];
  const rods: number[][] = [
    [80, 14, 58, 37], [80, 14, 102, 37],
    [58, 37, 44, 60], [102, 37, 116, 60],
    [58, 37, 80, 60], [102, 37, 80, 60],
    [44, 60, 58, 83], [116, 60, 102, 83],
    [44, 60, 80, 60], [116, 60, 80, 60],
    [80, 60, 58, 83], [80, 60, 102, 83],
    [58, 83, 80, 106], [102, 83, 80, 106],
    [80, 14, 80, 60], [80, 60, 80, 106],
  ];
  return (
    <>
      <ellipse cx="80" cy="114" rx="30" ry="4" fill="var(--st-dp)" opacity="0.2" />
      <g stroke="var(--st-sh)" strokeWidth="3" strokeLinecap="round" opacity="0.85">
        {rods.map((r, i) => (
          <line key={i} x1={r[0]} y1={r[1]} x2={r[2]} y2={r[3]} />
        ))}
      </g>
      {nodes.map((n, i) => {
        const gold = i === 8;
        return (
          <g key={i}>
            <circle cx={n[0]} cy={n[1]} r="9" fill={gold ? "var(--gd)" : "var(--st)"} />
            <circle cx={n[0] - 2.5} cy={n[1] - 2.5} r="3" fill={gold ? "var(--gd-hi)" : "var(--st-hi)"} opacity="0.85" />
            <path
              d={`M${n[0] - 6} ${n[1] + 5} A9 9 0 0 0 ${n[0] + 7} ${n[1] + 5}`}
              fill="none"
              stroke={gold ? "var(--gd-dp)" : "var(--st-dp)"}
              strokeWidth="1.4"
              opacity="0.5"
            />
          </g>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Neuschwanstein — Schwangau
// ---------------------------------------------------------------------------
export function neuschwanstein(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="56" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <path d="M18 108 L44 92 L120 92 L142 108 Z" fill="var(--st-dp)" opacity="0.28" />
      <rect x="52" y="52" width="40" height="52" fill="var(--st)" />
      <rect x="52" y="52" width="1.6" height="52" fill="var(--st-hi)" />
      <rect x="90.4" y="52" width="1.6" height="52" fill="var(--st-dp)" opacity="0.5" />
      {[60, 74, 88].map((y) => (
        <g key={y}>
          {[57, 67, 78].map((x) => (
            <path key={x} d={archPath(x, y, 5, 8)} fill="var(--st-dp)" opacity="0.45" />
          ))}
        </g>
      ))}
      <rect x="40" y="60" width="12" height="44" fill="var(--st)" />
      {cone(46, 60, 14, 16)}
      {finial(46, 44)}
      <rect x="66" y="42" width="10" height="14" fill="var(--st)" />
      {cone(71, 42, 12, 13)}
      {finial(71, 29)}
      <rect x="92" y="36" width="16" height="68" fill="var(--st)" />
      <rect x="92" y="36" width="1.6" height="68" fill="var(--st-hi)" />
      {cone(100, 36, 20, 20)}
      {finial(100, 16)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Reichstag — Berlin
// ---------------------------------------------------------------------------
export function reichstag(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="20" y="98" width="120" height="8" rx="2" fill="var(--st)" />
      <rect x="20" y="98" width="120" height="1.4" fill="var(--st-hi)" />
      <rect x="26" y="60" width="108" height="38" fill="var(--st)" />
      {[28, 120].map((x) => (
        <g key={x}>
          <rect x={x} y="50" width="12" height="48" fill="var(--st)" />
          <rect x={x} y="50" width="12" height="1.4" fill="var(--st-hi)" />
        </g>
      ))}
      {[62, 72, 82, 92].map((x) => (
        <rect key={x} x={x} y="66" width="5" height="30" fill="var(--st)" />
      ))}
      <rect x="56" y="62" width="48" height="5" fill="var(--st)" />
      <path d="M54 62 L80 48 L106 62 Z" fill="var(--st)" />
      <path d="M54 62 L80 48 L80 62 Z" fill="var(--st-hi)" opacity="0.3" />
      <path d="M68 60 Q68 40 80 38 Q92 40 92 60 Z" fill="var(--gd)" />
      <path d="M72 58 Q72 44 80 42" fill="none" stroke="var(--gd-hi)" strokeWidth="1" opacity="0.6" />
      <g stroke="var(--gd-dp)" strokeWidth="0.6" opacity="0.5">
        <line x1="80" y1="39" x2="80" y2="60" />
        <line x1="74" y1="43" x2="74" y2="60" />
        <line x1="86" y1="43" x2="86" y2="60" />
      </g>
      <rect x="66" y="59" width="28" height="3" fill="var(--gd-sh)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Frauenkirche — Dresden
// ---------------------------------------------------------------------------
export function frauenkircheDresden(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="48" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="34" y="98" width="92" height="8" rx="2" fill="var(--st)" />
      <rect x="42" y="66" width="76" height="32" fill="var(--st)" />
      <rect x="42" y="66" width="1.6" height="32" fill="var(--st-hi)" />
      <rect x="116.4" y="66" width="1.6" height="32" fill="var(--st-dp)" opacity="0.5" />
      {[38, 118].map((x) => (
        <rect key={x} x={x} y="70" width="6" height="28" fill="var(--st)" />
      ))}
      {[50, 66, 82, 98].map((x) => (
        <path key={x} d={archPath(x, 78, 8, 16)} fill="var(--st-dp)" opacity="0.4" />
      ))}
      <rect x="58" y="40" width="44" height="26" fill="var(--st)" />
      <path d="M56 40 Q56 12 80 10 Q104 12 104 40 Z" fill="var(--st)" />
      <path d="M60 38 Q60 18 80 15" fill="none" stroke="var(--st-hi)" strokeWidth="1.2" opacity="0.5" />
      <rect x="72" y="6" width="16" height="6" rx="1" fill="var(--st)" />
      {finial(80, 6)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Notre-Dame — Paris
// ---------------------------------------------------------------------------
export function notreDame(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="56" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="28" y="100" width="104" height="6" rx="2" fill="var(--st)" />
      <rect x="34" y="40" width="92" height="60" fill="var(--st)" />
      <rect x="34" y="40" width="92" height="1.6" fill="var(--st-hi)" />
      {[36, 100].map((x) => (
        <g key={x}>
          <rect x={x} y="30" width="24" height="70" fill="var(--st)" />
          <rect x={x} y="30" width="1.6" height="70" fill="var(--st-hi)" />
          <rect x={x + 22.4} y="30" width="1.6" height="70" fill="var(--st-dp)" opacity="0.5" />
          <path d={archPath(x + 6, 44, 12, 16)} fill="var(--st-dp)" opacity="0.45" />
        </g>
      ))}
      <circle cx="80" cy="56" r="8" fill="var(--st-dp)" opacity="0.5" />
      <circle cx="80" cy="56" r="8" fill="none" stroke="var(--gd)" strokeWidth="1.4" />
      <circle cx="80" cy="56" r="3" fill="none" stroke="var(--gd)" strokeWidth="1" />
      {[62, 74, 86].map((x, i) => (
        <path key={x} d={archPath(x - (i === 1 ? 1 : 0), 78, i === 1 ? 14 : 10, 22)} fill="var(--st-dp)" opacity="0.5" />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sacré-Cœur — Paris
// ---------------------------------------------------------------------------
export function sacreCoeur(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="52" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <path d="M22 108 L48 98 L112 98 L138 108 Z" fill="var(--st-dp)" opacity="0.25" />
      <rect x="40" y="66" width="80" height="34" fill="var(--st)" />
      <rect x="40" y="66" width="80" height="1.6" fill="var(--st-hi)" />
      {[46, 74, 102].map((x) => (
        <path key={x} d={archPath(x, 80, 12, 20)} fill="var(--st-dp)" opacity="0.4" />
      ))}
      {dome(52, 66, 9, 12)}
      {finial(52, 42)}
      {dome(108, 66, 9, 12)}
      {finial(108, 42)}
      <rect x="70" y="52" width="20" height="14" fill="var(--st)" />
      {dome(80, 52, 15, 22, false)}
      <rect x="76" y="18" width="8" height="10" fill="var(--st)" />
      {dome(80, 18, 6, 8)}
      {finial(80, 4)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Mont-Saint-Michel — Normandy
// ---------------------------------------------------------------------------
export function montSaintMichel(): ReactElement {
  return (
    <>
      <rect x="6" y="102" width="148" height="8" rx="2" fill="var(--st-dp)" opacity="0.25" />
      <path d="M40 104 L80 30 L120 104 Z" fill="var(--st-dp)" opacity="0.35" />
      <path d="M46 104 L80 40 L114 104 Z" fill="var(--st)" />
      {[92, 80, 68].map((y, i) => (
        <rect key={y} x={72 - i * 3} y={y} width={16 + i * 6} height="8" fill="var(--st)" opacity={0.95 - i * 0.06} />
      ))}
      {[92, 80, 68].map((y, i) => (
        <rect key={`e${y}`} x={72 - i * 3} y={y} width={16 + i * 6} height="1.2" fill="var(--st-hi)" />
      ))}
      <rect x="74" y="46" width="12" height="22" fill="var(--st)" />
      <rect x="74" y="46" width="1.4" height="22" fill="var(--st-hi)" />
      {cone(80, 46, 14, 18)}
      <rect x="79" y="12" width="2" height="16" fill="var(--gd)" />
      <circle cx="80" cy="11" r="2" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// St. Peter's Basilica — Vatican
// ---------------------------------------------------------------------------
export function stPeters(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="20" y="94" width="120" height="10" rx="2" fill="var(--st)" />
      <rect x="20" y="94" width="120" height="1.6" fill="var(--st-hi)" />
      {[28, 40, 52, 108, 120, 132].map((x) => (
        <rect key={x} x={x} y="70" width="6" height="24" fill="var(--st)" />
      ))}
      <rect x="24" y="64" width="112" height="6" fill="var(--st)" />
      <rect x="58" y="70" width="44" height="24" fill="var(--st)" />
      <rect x="66" y="52" width="28" height="16" fill="var(--st)" />
      {dome(80, 52, 22, 30, false)}
      <path d="M74 22 Q74 30 80 30 Q86 30 86 22 Z" fill="var(--st)" />
      <rect x="76" y="14" width="8" height="8" fill="var(--st)" />
      {finial(80, 12)}
      <g stroke="var(--st-sh)" strokeWidth="0.7" opacity="0.4">
        <line x1="80" y1="24" x2="80" y2="52" />
        <line x1="68" y1="34" x2="70" y2="52" />
        <line x1="92" y1="34" x2="90" y2="52" />
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// St. Mark's Basilica — Venice
// ---------------------------------------------------------------------------
export function stMarks(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="58" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="24" y="96" width="112" height="8" rx="2" fill="var(--st)" />
      <rect x="30" y="60" width="100" height="36" fill="var(--st)" />
      <rect x="30" y="60" width="100" height="1.6" fill="var(--st-hi)" />
      {[36, 58, 80, 102].map((x, i) => (
        <path key={x} d={archPath(x, 70, i === 1 || i === 2 ? 20 : 18, 26)} fill="var(--st-dp)" opacity="0.42" />
      ))}
      {[44, 80, 116].map((cx, i) => (
        <g key={cx}>
          {dome(cx, 60, i === 1 ? 15 : 11, i === 1 ? 20 : 15)}
          {finial(cx, i === 1 ? 26 : 32)}
        </g>
      ))}
      {[62, 98].map((cx) => (
        <g key={cx}>
          {dome(cx, 60, 9, 12)}
          {finial(cx, 40)}
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Trevi Fountain — Rome
// ---------------------------------------------------------------------------
export function treviFountain(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="58" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="28" y="30" width="104" height="66" fill="var(--st)" />
      <rect x="28" y="30" width="104" height="1.6" fill="var(--st-hi)" />
      <rect x="28" y="24" width="104" height="7" rx="1.5" fill="var(--st)" />
      {[36, 46, 114, 124].map((x) => (
        <rect key={x} x={x} y="40" width="6" height="40" fill="var(--st)" />
      ))}
      <path d={archPath(62, 42, 36, 46)} fill="var(--st-dp)" />
      <rect x="72" y="62" width="16" height="26" fill="var(--st-sh)" />
      <rect x="72" y="62" width="16" height="1.4" fill="var(--st)" />
      {/* water basin */}
      <path d="M18 96 Q40 108 80 106 Q120 108 142 96 L142 106 L18 106 Z" fill="var(--gd)" opacity="0.85" />
      <path d="M24 98 Q50 106 80 104 Q110 106 136 98" fill="none" stroke="var(--gd-hi)" strokeWidth="1" opacity="0.6" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Duomo di Milano — Milan
// ---------------------------------------------------------------------------
export function duomoMilano(): ReactElement {
  const spikes = [30, 40, 50, 60, 100, 110, 120, 130];
  return (
    <>
      <ellipse cx="80" cy="114" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="24" y="98" width="112" height="8" rx="2" fill="var(--st)" />
      <rect x="30" y="56" width="100" height="42" fill="var(--st)" />
      <rect x="30" y="56" width="100" height="1.6" fill="var(--st-hi)" />
      {[38, 54, 70, 86, 102, 118].map((x) => (
        <path key={x} d={archPath(x, 68, 8, 30)} fill="var(--st-dp)" opacity="0.4" />
      ))}
      {spikes.map((cx) => (
        <g key={cx}>
          <rect x={cx - 1.5} y="44" width="3" height="12" fill="var(--st)" />
          {cone(cx, 44, 5, 10)}
        </g>
      ))}
      <rect x="74" y="34" width="12" height="22" fill="var(--st)" />
      {cone(80, 34, 16, 20)}
      <rect x="79" y="12" width="2" height="10" fill="var(--gd)" />
      <path d="M77 14 Q80 8 83 14 Q80 12 80 18 Q80 12 77 14 Z" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// London Eye — London
// ---------------------------------------------------------------------------
export function londonEye(): ReactElement {
  const cx = 80, cy = 56, r = 42;
  const spokes = Array.from({ length: 16 }, (_, i) => (i * Math.PI * 2) / 16);
  const caps = Array.from({ length: 16 }, (_, i) => (i * Math.PI * 2) / 16);
  return (
    <>
      <ellipse cx="80" cy="114" rx="40" ry="4" fill="var(--st-dp)" opacity="0.2" />
      <path d="M64 108 L78 58 L82 58 L96 108 Z" fill="var(--st-sh)" />
      <path d="M64 108 L78 58 L80 58 L72 108 Z" fill="var(--st)" />
      <g stroke="var(--st-sh)" strokeWidth="0.9" opacity="0.7">
        {spokes.map((a, i) => (
          <line key={i} x1={cx} y1={cy} x2={cx + Math.cos(a) * r} y2={cy + Math.sin(a) * r} />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--st)" strokeWidth="3.5" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--st-hi)" strokeWidth="1" opacity="0.5" />
      {caps.map((a, i) => (
        <circle key={i} cx={cx + Math.cos(a) * r} cy={cy + Math.sin(a) * r} r="2.4" fill="var(--gd)" />
      ))}
      <circle cx={cx} cy={cy} r="4" fill="var(--st)" />
      <circle cx={cx} cy={cy} r="1.6" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Stonehenge — Wiltshire
// ---------------------------------------------------------------------------
export function stonehenge(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="112" rx="64" ry="6" fill="var(--st-dp)" opacity="0.2" />
      <rect x="10" y="104" width="140" height="4" rx="2" fill="var(--st-dp)" opacity="0.25" />
      {/* back stones (smaller) */}
      {[52, 80, 108].map((x) => (
        <rect key={`b${x}`} x={x - 4} y="60" width="8" height="44" rx="1.5" fill="var(--st-sh)" />
      ))}
      {/* front trilithons */}
      {[
        [24, 42], [96, 42],
      ].map(([x1, x2]) => (
        <g key={x1}>
          <rect x={x1} y="52" width="12" height="52" rx="1.5" fill="var(--st)" />
          <rect x={x1} y="52" width="1.6" height="52" fill="var(--st-hi)" />
          <rect x={x2} y="52" width="12" height="52" rx="1.5" fill="var(--st)" />
          <rect x={x2 + 10.4} y="52" width="1.6" height="52" fill="var(--st-dp)" opacity="0.5" />
          <rect x={x1 - 2} y="44" width={x2 - x1 + 14} height="9" rx="1.5" fill="var(--st)" />
          <rect x={x1 - 2} y="44" width={x2 - x1 + 14} height="1.4" fill="var(--st-hi)" />
        </g>
      ))}
      {/* lone leaning stone */}
      <rect x="128" y="62" width="9" height="42" rx="1.5" fill="var(--st)" transform="rotate(6 132 104)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Edinburgh Castle
// ---------------------------------------------------------------------------
export function edinburghCastle(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <path d="M12 106 L34 92 L128 92 L148 106 Z" fill="var(--st-dp)" opacity="0.3" />
      <rect x="36" y="66" width="88" height="38" fill="var(--st)" />
      <rect x="36" y="66" width="88" height="1.6" fill="var(--st-hi)" />
      {/* crenellations */}
      {[36, 44, 52, 60, 68, 76, 84, 92, 100, 108, 116].map((x) => (
        <rect key={x} x={x} y="62" width="4" height="5" fill="var(--st)" />
      ))}
      {/* keep */}
      <rect x="70" y="44" width="22" height="60" fill="var(--st)" />
      <rect x="70" y="44" width="1.6" height="60" fill="var(--st-hi)" />
      {[70, 76, 82, 88].map((x) => (
        <rect key={x} x={x} y="40" width="3.5" height="5" fill="var(--st)" />
      ))}
      {[48, 108].map((x) => (
        <g key={x}>
          <rect x={x} y="54" width="12" height="50" fill="var(--st)" />
          {[x, x + 4, x + 8].map((cx) => (
            <rect key={cx} x={cx} y="50" width="3" height="5" fill="var(--st)" />
          ))}
        </g>
      ))}
      {[54, 76, 82, 88, 114].map((x) => (
        <path key={x} d={archPath(x, 76, 6, 14)} fill="var(--st-dp)" opacity="0.42" />
      ))}
      {finial(81, 40)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cliffs of Moher — Clare
// ---------------------------------------------------------------------------
export function cliffsOfMoher(): ReactElement {
  return (
    <>
      {/* sea */}
      <rect x="6" y="96" width="148" height="14" rx="3" fill="var(--gd)" opacity="0.28" />
      <path d="M6 100 Q40 96 80 100 Q120 104 154 100" fill="none" stroke="var(--gd)" strokeWidth="1.2" opacity="0.5" />
      {/* cliff mass */}
      <path d="M10 96 L10 52 Q30 40 58 46 L92 40 Q120 36 150 50 L150 96 Z" fill="var(--st)" />
      <path d="M10 52 Q30 40 58 46 L92 40 Q120 36 150 50" fill="none" stroke="var(--st-hi)" strokeWidth="1.4" opacity="0.6" />
      {/* strata */}
      <g stroke="var(--st-dp)" strokeWidth="0.8" opacity="0.35">
        <path d="M12 66 Q60 60 148 66" fill="none" />
        <path d="M12 80 Q60 76 148 82" fill="none" />
      </g>
      {/* O'Brien's Tower */}
      <rect x="110" y="26" width="12" height="18" fill="var(--st)" />
      <rect x="110" y="26" width="1.4" height="18" fill="var(--st-hi)" />
      {[110, 114, 118].map((x) => (
        <rect key={x} x={x} y="22" width="3" height="5" fill="var(--st)" />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sagrada Família — Barcelona
// ---------------------------------------------------------------------------
export function sagradaFamilia(): ReactElement {
  const towers: [number, number][] = [[46, 44], [62, 24], [80, 12], [98, 24], [114, 44]];
  return (
    <>
      <ellipse cx="80" cy="114" rx="58" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="30" y="98" width="100" height="8" rx="2" fill="var(--st)" />
      <rect x="34" y="72" width="92" height="26" fill="var(--st)" />
      <rect x="34" y="72" width="92" height="1.6" fill="var(--st-hi)" />
      {towers.map(([cx, topY]) => (
        <g key={cx}>
          <path d={`M${cx - 7} 74 L${cx - 3.5} ${topY + 8} Q${cx} ${topY} ${cx + 3.5} ${topY + 8} L${cx + 7} 74 Z`} fill="var(--st)" />
          <path d={`M${cx - 7} 74 L${cx - 3.5} ${topY + 8} Q${cx} ${topY} ${cx} ${topY} L${cx} 74 Z`} fill="var(--st-hi)" opacity="0.25" />
          <g fill="var(--st-dp)" opacity="0.4">
            {[74 - 8, 74 - 20, 74 - 32].map((y) => (
              y > topY + 6 ? <circle key={y} cx={cx} cy={y} r="1.3" /> : null
            ))}
          </g>
          <circle cx={cx} cy={topY + 2} r="2" fill="var(--gd)" />
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Alhambra — Granada
// ---------------------------------------------------------------------------
export function alhambra(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      {/* reflecting pool */}
      <rect x="20" y="98" width="120" height="8" rx="2" fill="var(--gd)" opacity="0.22" />
      <rect x="26" y="64" width="108" height="34" fill="var(--st)" />
      <rect x="26" y="64" width="108" height="1.6" fill="var(--st-hi)" />
      {/* horseshoe arcade */}
      {[32, 48, 64, 96, 112].map((x) => (
        <path key={x} d={archPath(x, 74, 12, 24)} fill="var(--st-dp)" opacity="0.42" />
      ))}
      {/* battlements */}
      {[26, 34, 42, 50, 58, 102, 110, 118, 126].map((x) => (
        <rect key={x} x={x} y="60" width="4" height="5" fill="var(--st)" />
      ))}
      {/* tower */}
      <rect x="70" y="42" width="20" height="56" fill="var(--st)" />
      <rect x="70" y="42" width="1.6" height="56" fill="var(--st-hi)" />
      {[70, 74.5, 79, 83.5, 88].map((x) => (
        <rect key={x} x={x} y="38" width="3" height="5" fill="var(--st)" />
      ))}
      <path d={archPath(76, 56, 8, 12)} fill="var(--st-dp)" opacity="0.5" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Guggenheim — Bilbao
// ---------------------------------------------------------------------------
export function guggenheimBilbao(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="112" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="10" y="100" width="140" height="6" rx="2" fill="var(--gd)" opacity="0.2" />
      <path d="M18 100 L22 70 Q40 60 60 66 L66 100 Z" fill="var(--st)" />
      <path d="M56 100 L60 54 Q84 44 104 58 L104 100 Z" fill="var(--st)" />
      <path d="M56 100 L60 54 Q72 49 82 51 L80 100 Z" fill="var(--st-hi)" opacity="0.22" />
      <path d="M96 100 L100 66 Q122 56 144 70 L144 100 Z" fill="var(--st)" />
      <path d="M40 66 Q64 58 96 64" fill="none" stroke="var(--gd)" strokeWidth="1.4" opacity="0.55" />
      <path d="M22 84 Q70 78 140 86" fill="none" stroke="var(--st-dp)" strokeWidth="0.8" opacity="0.35" />
      <path d="M62 100 L64 60 Q66 58 70 58" fill="none" stroke="var(--st-hi)" strokeWidth="1" opacity="0.5" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Torre de Belém — Lisbon
// ---------------------------------------------------------------------------
export function belemTower(): ReactElement {
  return (
    <>
      <rect x="8" y="98" width="144" height="10" rx="3" fill="var(--gd)" opacity="0.2" />
      <path d="M8 100 Q40 97 80 100 Q120 103 152 100" fill="none" stroke="var(--gd)" strokeWidth="1" opacity="0.5" />
      {/* bastion */}
      <rect x="44" y="82" width="72" height="18" fill="var(--st)" />
      <rect x="44" y="82" width="72" height="1.4" fill="var(--st-hi)" />
      {[44, 52, 60, 68, 76, 84, 92, 100, 108].map((x) => (
        <rect key={x} x={x} y="78" width="4" height="5" fill="var(--st)" />
      ))}
      {/* tower */}
      <rect x="66" y="34" width="28" height="48" fill="var(--st)" />
      <rect x="66" y="34" width="1.6" height="48" fill="var(--st-hi)" />
      <rect x="92.4" y="34" width="1.6" height="48" fill="var(--st-dp)" opacity="0.5" />
      {[42, 54, 66].map((y) => (
        <rect key={y} x="74" y={y} width="12" height="2" fill="var(--st-sh)" opacity="0.5" />
      ))}
      <path d={archPath(74, 66, 12, 16)} fill="var(--st-dp)" opacity="0.45" />
      {[66, 71, 76, 81, 86, 91].map((x) => (
        <rect key={x} x={x} y="30" width="3.4" height="5" fill="var(--st)" />
      ))}
      {/* corner turrets */}
      {[62, 94].map((x) => (
        <g key={x}>
          <rect x={x} y="46" width="6" height="10" fill="var(--st)" />
          {dome(x + 3, 46, 4, 5)}
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Torre dos Clérigos — Porto
// ---------------------------------------------------------------------------
export function clerigosTower(): ReactElement {
  const tiers = [88, 74, 60, 46];
  return (
    <>
      <ellipse cx="80" cy="114" rx="32" ry="4" fill="var(--st-dp)" opacity="0.2" />
      <rect x="60" y="98" width="40" height="8" rx="2" fill="var(--st)" />
      <rect x="66" y="88" width="28" height="12" fill="var(--st)" />
      {tiers.map((y, i) => {
        const w = 24 - i * 3;
        const x = 80 - w / 2;
        return (
          <g key={y}>
            <rect x={x} y={y} width={w} height="12" fill="var(--st)" />
            <rect x={x} y={y} width="1.4" height="12" fill="var(--st-hi)" />
            <rect x={x + w - 1.4} y={y} width="1.4" height="12" fill="var(--st-dp)" opacity="0.5" />
            <path d={archPath(80 - (w - 8) / 2, y + 2, w - 8, 8)} fill="var(--st-dp)" opacity="0.45" />
          </g>
        );
      })}
      <rect x="74" y="34" width="12" height="12" fill="var(--st)" />
      {cone(80, 34, 16, 16)}
      {finial(80, 18)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Grand-Place — Brussels
// ---------------------------------------------------------------------------
export function grandPlace(): ReactElement {
  const houses = [22, 40, 58, 100, 118];
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="14" y="100" width="132" height="6" rx="2" fill="var(--st)" />
      {houses.map((x, i) => {
        const h = 44 + (i % 2) * 8;
        const y = 100 - h;
        return (
          <g key={x}>
            <rect x={x} y={y} width="16" height={h} fill="var(--st)" />
            <rect x={x} y={y} width="1.4" height={h} fill="var(--st-hi)" />
            {/* stepped gable */}
            <path d={`M${x} ${y} L${x + 3} ${y - 4} L${x + 6} ${y} L${x + 8} ${y - 5} L${x + 10} ${y} L${x + 13} ${y - 4} L${x + 16} ${y} Z`} fill="var(--st)" />
            {[y + 8, y + 20, y + 32].map((wy) => (
              wy < 96 ? <rect key={wy} x={x + 4} y={wy} width="8" height="6" fill="var(--st-dp)" opacity="0.4" /> : null
            ))}
            <circle cx={x + 8} cy={y - 5} r="1.2" fill="var(--gd)" />
          </g>
        );
      })}
      {/* town-hall spire */}
      <rect x="74" y="40" width="12" height="60" fill="var(--st)" />
      <rect x="74" y="40" width="1.4" height="60" fill="var(--st-hi)" />
      {cone(80, 40, 16, 26)}
      <rect x="79" y="6" width="2" height="10" fill="var(--gd)" />
      <circle cx="80" cy="5" r="2" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Windmills — Kinderdijk
// ---------------------------------------------------------------------------
export function windmillsKinderdijk(): ReactElement {
  return (
    <>
      <rect x="8" y="98" width="144" height="10" rx="3" fill="var(--gd)" opacity="0.2" />
      <path d="M8 100 Q40 98 80 100 Q120 102 152 100" fill="none" stroke="var(--gd)" strokeWidth="1" opacity="0.5" />
      {/* mill body */}
      <path d="M66 98 L70 52 L90 52 L94 98 Z" fill="var(--st)" />
      <path d="M66 98 L70 52 L80 52 L78 98 Z" fill="var(--st-hi)" opacity="0.22" />
      {/* cap */}
      <path d="M68 52 Q80 40 92 52 Z" fill="var(--st)" />
      {/* door + window */}
      <path d={archPath(76, 84, 8, 14)} fill="var(--st-dp)" opacity="0.45" />
      <rect x="77" y="64" width="6" height="6" fill="var(--st-dp)" opacity="0.4" />
      {/* sails (cross) */}
      <g transform="rotate(20 80 46)" stroke="var(--st-sh)" strokeWidth="2.4" strokeLinecap="round">
        <line x1="80" y1="10" x2="80" y2="82" />
        <line x1="44" y1="46" x2="116" y2="46" />
      </g>
      <g transform="rotate(20 80 46)" stroke="var(--gd)" strokeWidth="0.9" opacity="0.7">
        <line x1="76" y1="14" x2="76" y2="78" />
        <line x1="48" y1="42" x2="112" y2="42" />
      </g>
      <circle cx="80" cy="46" r="3" fill="var(--st)" />
      <circle cx="80" cy="46" r="1.3" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Rijksmuseum — Amsterdam
// ---------------------------------------------------------------------------
export function rijksmuseum(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="18" y="100" width="124" height="6" rx="2" fill="var(--st)" />
      <rect x="24" y="56" width="112" height="44" fill="var(--st)" />
      <rect x="24" y="56" width="112" height="1.6" fill="var(--st-hi)" />
      {/* central arch passage */}
      <path d={archPath(70, 66, 20, 34)} fill="var(--st-dp)" />
      {/* twin turrets with pointed caps */}
      {[30, 116].map((x) => (
        <g key={x}>
          <rect x={x} y="42" width="14" height="58" fill="var(--st)" />
          <rect x={x} y="42" width="1.6" height="58" fill="var(--st-hi)" />
          {cone(x + 7, 42, 18, 16)}
          {finial(x + 7, 26)}
        </g>
      ))}
      {/* windows */}
      {[46, 56, 100, 110].map((x) => (
        <g key={x}>
          {[62, 76].map((y) => (
            <path key={y} d={archPath(x, y, 7, 10)} fill="var(--st-dp)" opacity="0.4" />
          ))}
        </g>
      ))}
      {/* central gable */}
      <path d="M64 56 L80 44 L96 56 Z" fill="var(--st)" />
      <path d="M64 56 L80 44 L80 56 Z" fill="var(--st-hi)" opacity="0.3" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Charles Bridge — Prague
// ---------------------------------------------------------------------------
export function charlesBridge(): ReactElement {
  return (
    <>
      <rect x="6" y="94" width="148" height="14" rx="3" fill="var(--gd)" opacity="0.22" />
      {/* deck */}
      <rect x="10" y="66" width="140" height="10" fill="var(--st)" />
      <rect x="10" y="66" width="140" height="1.4" fill="var(--st-hi)" />
      {/* arches over water */}
      {[16, 44, 72, 100, 128].map((x) => (
        <path key={x} d={`M${x} 94 Q${x + 14} 74 ${x + 28} 94 Z`} fill="var(--st-dp)" opacity="0.4" />
      ))}
      {/* gate tower */}
      <rect x="60" y="30" width="24" height="36" fill="var(--st)" />
      <rect x="60" y="30" width="1.6" height="36" fill="var(--st-hi)" />
      <path d={archPath(66, 44, 12, 22)} fill="var(--st-dp)" opacity="0.5" />
      <path d="M58 30 L72 16 L86 30 Z" fill="var(--st)" />
      {[62, 68, 74, 80].map((x) => (
        <rect key={x} x={x} y="27" width="3" height="4" fill="var(--st)" />
      ))}
      {finial(72, 15)}
      {/* statues along the rail */}
      {[24, 38, 96, 110, 126].map((x) => (
        <g key={x} fill="var(--gd)">
          <circle cx={x} cy="60" r="1.5" />
          <rect x={x - 1.4} y="61" width="2.8" height="6" rx="1" />
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Prague Castle
// ---------------------------------------------------------------------------
export function pragueCastle(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <path d="M10 106 L10 88 L150 88 L150 106 Z" fill="var(--st-dp)" opacity="0.25" />
      {/* palace wall */}
      <rect x="18" y="72" width="124" height="24" fill="var(--st)" />
      <rect x="18" y="72" width="124" height="1.6" fill="var(--st-hi)" />
      {[24, 36, 48, 108, 120, 132].map((x) => (
        <rect key={x} x={x} y="80" width="7" height="16" fill="var(--st-dp)" opacity="0.4" />
      ))}
      {/* St Vitus twin Gothic spires */}
      {[70, 90].map((cx) => (
        <g key={cx}>
          <rect x={cx - 6} y="40" width="12" height="32" fill="var(--st)" />
          <rect x={cx - 6} y="40" width="1.4" height="32" fill="var(--st-hi)" />
          {cone(cx, 40, 14, 26)}
          {finial(cx, 14)}
        </g>
      ))}
      {/* central gable + rose */}
      <rect x="74" y="52" width="12" height="20" fill="var(--st)" />
      <circle cx="80" cy="60" r="3.5" fill="var(--st-dp)" opacity="0.5" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Astronomical Clock — Prague
// ---------------------------------------------------------------------------
export function astronomicalClock(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="40" ry="4" fill="var(--st-dp)" opacity="0.2" />
      {/* tower wall */}
      <rect x="52" y="14" width="56" height="92" fill="var(--st)" />
      <rect x="52" y="14" width="1.6" height="92" fill="var(--st-hi)" />
      <rect x="106.4" y="14" width="1.6" height="92" fill="var(--st-dp)" opacity="0.5" />
      {/* upper clock */}
      <circle cx="80" cy="40" r="15" fill="var(--st-sh)" />
      <circle cx="80" cy="40" r="15" fill="none" stroke="var(--gd)" strokeWidth="2.2" />
      <circle cx="80" cy="40" r="9" fill="none" stroke="var(--gd)" strokeWidth="1" />
      <g stroke="var(--gd)" strokeWidth="1.1" strokeLinecap="round">
        <line x1="80" y1="40" x2="80" y2="30" />
        <line x1="80" y1="40" x2="88" y2="44" />
      </g>
      <circle cx="80" cy="40" r="1.6" fill="var(--gd)" />
      {/* lower calendar dial */}
      <circle cx="80" cy="76" r="12" fill="var(--st-sh)" />
      <circle cx="80" cy="76" r="12" fill="none" stroke="var(--gd)" strokeWidth="1.6" />
      <circle cx="80" cy="76" r="4" fill="var(--gd)" opacity="0.6" />
      {finial(80, 14)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Schloss Schönbrunn — Vienna
// ---------------------------------------------------------------------------
export function schonbrunn(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="64" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="12" y="100" width="136" height="6" rx="2" fill="var(--st)" />
      <rect x="16" y="66" width="128" height="34" fill="var(--st)" />
      <rect x="16" y="66" width="128" height="1.6" fill="var(--st-hi)" />
      {/* central pavilion */}
      <rect x="64" y="52" width="32" height="48" fill="var(--st)" />
      <rect x="64" y="52" width="1.6" height="48" fill="var(--st-hi)" />
      <path d="M62 52 L80 40 L98 52 Z" fill="var(--st)" />
      <path d="M62 52 L80 40 L80 52 Z" fill="var(--st-hi)" opacity="0.3" />
      <path d={archPath(72, 78, 16, 22)} fill="var(--st-dp)" opacity="0.5" />
      {/* window rows */}
      {[72, 84].map((y) => (
        <g key={y}>
          {[22, 32, 42, 52, 106, 116, 126, 136].map((x) => (
            <rect key={x} x={x} y={y} width="6" height="8" fill="var(--st-dp)" opacity="0.38" />
          ))}
        </g>
      ))}
      <circle cx="80" cy="46" r="2" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Stephansdom — Vienna
// ---------------------------------------------------------------------------
export function stephansdom(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="56" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="30" y="98" width="100" height="8" rx="2" fill="var(--st)" />
      {/* nave with patterned roof */}
      <rect x="40" y="70" width="66" height="28" fill="var(--st)" />
      <path d="M40 70 L48 58 L106 58 L106 70 Z" fill="var(--gd)" opacity="0.55" />
      <g stroke="var(--gd-dp)" strokeWidth="0.7" opacity="0.5">
        <line x1="56" y1="58" x2="52" y2="70" />
        <line x1="70" y1="58" x2="70" y2="70" />
        <line x1="86" y1="58" x2="90" y2="70" />
      </g>
      {/* tall south tower */}
      <rect x="98" y="52" width="18" height="46" fill="var(--st)" />
      <rect x="98" y="52" width="1.6" height="46" fill="var(--st-hi)" />
      <path d="M98 52 L107 14 L116 52 Z" fill="var(--st)" />
      <path d="M98 52 L107 14 L107 52 Z" fill="var(--st-hi)" opacity="0.28" />
      <g stroke="var(--st-dp)" strokeWidth="0.7" opacity="0.5">
        <line x1="101" y1="46" x2="113" y2="46" />
        <line x1="103" y1="36" x2="111" y2="36" />
      </g>
      {finial(107, 12)}
      {/* small north tower */}
      <rect x="40" y="60" width="12" height="10" fill="var(--st)" />
      {dome(46, 60, 9, 11)}
      {finial(46, 40)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Kapellbrücke — Lucerne
// ---------------------------------------------------------------------------
export function chapelBridge(): ReactElement {
  return (
    <>
      <rect x="6" y="96" width="148" height="12" rx="3" fill="var(--gd)" opacity="0.22" />
      <path d="M6 100 Q40 98 80 100 Q120 102 154 100" fill="none" stroke="var(--gd)" strokeWidth="1" opacity="0.5" />
      {/* piers */}
      {[24, 52, 108, 136].map((x) => (
        <rect key={x} x={x} y="78" width="5" height="20" fill="var(--st-dp)" opacity="0.4" />
      ))}
      {/* covered deck */}
      <rect x="14" y="70" width="132" height="10" fill="var(--st)" />
      <path d="M14 70 L20 62 L146 62 L140 70 Z" fill="var(--st)" />
      <path d="M14 70 L20 62 L80 62 L80 70 Z" fill="var(--st-hi)" opacity="0.2" />
      {/* octagonal water tower */}
      <rect x="60" y="44" width="18" height="54" fill="var(--st)" />
      <rect x="60" y="44" width="1.6" height="54" fill="var(--st-hi)" />
      <rect x="78.4" y="44" width="1.6" height="54" fill="var(--st-dp)" opacity="0.5" />
      {[52, 66, 80].map((y) => (
        <rect key={y} x="65" y={y} width="8" height="6" fill="var(--st-dp)" opacity="0.4" />
      ))}
      {cone(69, 44, 22, 16)}
      {finial(69, 28)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Matterhorn — Zermatt
// ---------------------------------------------------------------------------
export function matterhorn(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="112" rx="64" ry="6" fill="var(--st-dp)" opacity="0.18" />
      {/* massif */}
      <path d="M14 106 L58 40 L74 56 L96 26 L146 106 Z" fill="var(--st)" />
      {/* shadowed east face */}
      <path d="M96 26 L146 106 L96 60 Z" fill="var(--st-dp)" opacity="0.4" />
      <path d="M58 40 L74 56 L54 70 L40 60 Z" fill="var(--st-sh)" opacity="0.5" />
      {/* snow cap highlight */}
      <path d="M96 26 L104 42 L92 44 L84 38 Z" fill="var(--st-hi)" />
      <path d="M96 26 L104 42 L96 44 Z" fill="var(--gd)" opacity="0.35" />
      {/* snow gullies */}
      <g stroke="var(--st-hi)" strokeWidth="1.4" opacity="0.55" strokeLinecap="round">
        <path d="M96 34 L86 70 M96 34 L104 66" fill="none" />
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// Wawel Castle — Kraków
// ---------------------------------------------------------------------------
export function wawelCastle(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <path d="M8 106 L8 92 L152 92 L152 106 Z" fill="var(--st-dp)" opacity="0.25" />
      {/* wall */}
      <rect x="18" y="70" width="124" height="22" fill="var(--st)" />
      <rect x="18" y="70" width="124" height="1.6" fill="var(--st-hi)" />
      {[24, 36, 48, 110, 122, 134].map((x) => (
        <rect key={x} x={x} y="78" width="7" height="14" fill="var(--st-dp)" opacity="0.4" />
      ))}
      {/* cathedral domes/towers */}
      <rect x="62" y="54" width="12" height="38" fill="var(--st)" />
      {dome(68, 54, 9, 12, true)}
      {finial(68, 30)}
      <rect x="84" y="48" width="14" height="44" fill="var(--st)" />
      <rect x="84" y="48" width="1.4" height="44" fill="var(--st-hi)" />
      {cone(91, 48, 18, 18)}
      {finial(91, 30)}
      {/* corner tower */}
      <rect x="30" y="58" width="14" height="34" fill="var(--st)" />
      {[30, 34.5, 39].map((x) => (
        <rect key={x} x={x} y="54" width="3" height="5" fill="var(--st)" />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Palace of Culture — Warsaw
// ---------------------------------------------------------------------------
export function warsawPalace(): ReactElement {
  const tiers: [number, number, number][] = [
    [40, 82, 80], [50, 66, 60], [58, 52, 44], [66, 40, 28],
  ];
  return (
    <>
      <ellipse cx="80" cy="114" rx="50" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="24" y="98" width="112" height="8" rx="2" fill="var(--st)" />
      {tiers.map(([y, top, w]) => {
        const x = 80 - w / 2;
        return (
          <g key={y}>
            <rect x={x} y={top} width={w} height={y - top + 18} fill="var(--st)" />
            <rect x={x} y={top} width="1.6" height={y - top + 18} fill="var(--st-hi)" />
            <rect x={x + w - 1.6} y={top} width="1.6" height={y - top + 18} fill="var(--st-dp)" opacity="0.5" />
          </g>
        );
      })}
      {/* window grid on the base */}
      {[36, 46, 84, 94, 104].map((x) => (
        <g key={x}>
          {[86, 92].map((wy) => (
            <rect key={wy} x={x} y={wy} width="4" height="5" fill="var(--st-dp)" opacity="0.4" />
          ))}
        </g>
      ))}
      {/* clock band + spire */}
      <circle cx="80" cy="46" r="3.5" fill="var(--gd)" />
      <rect x="76" y="24" width="8" height="16" fill="var(--st)" />
      {cone(80, 24, 12, 12)}
      <rect x="79" y="4" width="2" height="10" fill="var(--gd)" />
      <circle cx="80" cy="4" r="1.6" fill="var(--gd)" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Hungarian Parliament — Budapest
// ---------------------------------------------------------------------------
export function hungarianParliament(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="64" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="10" y="100" width="140" height="6" rx="2" fill="var(--st)" />
      <rect x="14" y="72" width="132" height="28" fill="var(--st)" />
      <rect x="14" y="72" width="132" height="1.6" fill="var(--st-hi)" />
      {[20, 30, 40, 120, 130, 140].map((x) => (
        <path key={x} d={archPath(x, 78, 6, 22)} fill="var(--st-dp)" opacity="0.4" />
      ))}
      {/* flanking spired pavilions */}
      {[48, 104].map((x) => (
        <g key={x}>
          <rect x={x} y="56" width="8" height="16" fill="var(--st)" />
          {cone(x + 4, 56, 12, 14)}
          {finial(x + 4, 42)}
        </g>
      ))}
      {/* central dome */}
      <rect x="66" y="52" width="28" height="20" fill="var(--st)" />
      {dome(80, 52, 18, 24)}
      <path d="M74 28 Q74 22 80 22 Q86 22 86 28 Z" fill="var(--st)" />
      {cone(80, 22, 8, 8)}
      {finial(80, 14)}
      <g stroke="var(--st-sh)" strokeWidth="0.7" opacity="0.4">
        <line x1="80" y1="30" x2="80" y2="52" />
        <line x1="70" y1="38" x2="72" y2="52" />
        <line x1="90" y1="38" x2="88" y2="52" />
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fisherman's Bastion — Budapest
// ---------------------------------------------------------------------------
export function fishermansBastion(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      {/* arcade terrace */}
      <rect x="18" y="76" width="124" height="22" fill="var(--st)" />
      <rect x="18" y="76" width="124" height="1.6" fill="var(--st-hi)" />
      {[24, 40, 56, 88, 104, 120].map((x) => (
        <path key={x} d={archPath(x, 82, 12, 16)} fill="var(--st-dp)" opacity="0.42" />
      ))}
      {/* conical turrets */}
      {[[34, 12], [80, 16], [126, 12]].map(([cx, extra]) => (
        <g key={cx}>
          <rect x={cx - 8} y={62 - extra} width="16" height={14 + extra} fill="var(--st)" />
          <rect x={cx - 8} y={62 - extra} width="1.4" height={14 + extra} fill="var(--st-hi)" />
          {cone(cx, 62 - extra, 20, 20)}
          {finial(cx, 42 - extra)}
        </g>
      ))}
      {/* small turrets */}
      {[57, 103].map((cx) => (
        <g key={cx}>
          <rect x={cx - 5} y="66" width="10" height="10" fill="var(--st)" />
          {cone(cx, 66, 12, 12)}
          {finial(cx, 54)}
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Parthenon — Athens
// ---------------------------------------------------------------------------
export function parthenon(): ReactElement {
  const cols = [30, 46, 62, 78, 94, 110, 126];
  return (
    <>
      <ellipse cx="80" cy="114" rx="60" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="16" y="98" width="128" height="8" rx="1.5" fill="var(--st)" />
      <rect x="16" y="98" width="128" height="1.4" fill="var(--st-hi)" />
      <rect x="22" y="92" width="116" height="6" fill="var(--st)" />
      {cols.map((cx) => (
        <g key={cx}>
          <rect x={cx - 4.5} y="52" width="9" height="40" fill="var(--st)" />
          <rect x={cx - 4.5} y="53" width="1.3" height="38" fill="var(--st-hi)" />
          <rect x={cx + 3.2} y="53" width="1.3" height="38" fill="var(--st-dp)" opacity="0.5" />
          <rect x={cx - 3} y="55" width="0.8" height="35" fill="var(--st-sh)" opacity="0.4" />
          <rect x={cx + 1.2} y="55" width="0.8" height="35" fill="var(--st-sh)" opacity="0.4" />
          <rect x={cx - 5.5} y="50" width="11" height="2.6" fill="var(--st)" />
        </g>
      ))}
      <rect x="18" y="44" width="124" height="7" fill="var(--st)" />
      <rect x="18" y="49" width="124" height="1.6" fill="var(--st-dp)" opacity="0.35" />
      {/* pediment */}
      <path d="M16 44 L80 22 L144 44 Z" fill="var(--st)" />
      <path d="M16 44 L80 22 L80 44 Z" fill="var(--st-hi)" opacity="0.28" />
      <path d="M28 42 L80 26 L132 42 Z" fill="var(--st-dp)" opacity="0.25" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Meteora — Thessaly
// ---------------------------------------------------------------------------
export function meteora(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="64" ry="5" fill="var(--st-dp)" opacity="0.18" />
      {/* rock pillars */}
      {[[34, 46], [80, 30], [122, 54]].map(([cx, topY]) => (
        <g key={cx}>
          <path d={`M${cx - 12} 106 Q${cx - 9} ${topY + 20} ${cx - 7} ${topY} L${cx + 7} ${topY} Q${cx + 9} ${topY + 20} ${cx + 12} 106 Z`} fill="var(--st)" />
          <path d={`M${cx - 12} 106 Q${cx - 9} ${topY + 20} ${cx - 7} ${topY} L${cx - 3} ${topY} Q${cx - 5} ${topY + 20} ${cx - 2} 106 Z`} fill="var(--st-hi)" opacity="0.22" />
          <path d={`M${cx + 2} 106 Q${cx + 5} ${topY + 20} ${cx + 7} ${topY} L${cx + 12} ${topY + 0} Q${cx + 9} ${topY + 24} ${cx + 12} 106 Z`} fill="var(--st-dp)" opacity="0.3" />
          {/* monastery on top */}
          <rect x={cx - 6} y={topY - 8} width="12" height="8" fill="var(--st)" />
          <path d={`M${cx - 6} ${topY - 8} L${cx} ${topY - 13} L${cx + 6} ${topY - 8} Z`} fill="var(--st)" />
          <circle cx={cx} cy={topY - 4} r="1.4" fill="var(--st-dp)" opacity="0.5" />
          {finial(cx, topY - 13)}
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Santorini — Cyclades
// ---------------------------------------------------------------------------
export function santorini(): ReactElement {
  return (
    <>
      {/* sea */}
      <rect x="6" y="98" width="148" height="12" rx="3" fill="var(--gd)" opacity="0.3" />
      <path d="M6 101 Q40 98 80 101 Q120 104 154 101" fill="none" stroke="var(--gd)" strokeWidth="1" opacity="0.5" />
      {/* cliff */}
      <path d="M8 98 L8 74 Q40 66 80 70 Q120 74 152 68 L152 98 Z" fill="var(--st-sh)" opacity="0.5" />
      {/* white cubic houses */}
      {[
        [22, 72, 16, 14], [40, 66, 14, 20], [58, 74, 18, 12], [92, 70, 16, 16], [112, 66, 14, 20], [128, 74, 16, 12],
      ].map(([x, y, w, h]) => (
        <g key={x}>
          <rect x={x} y={y} width={w} height={h} fill="var(--st)" />
          <rect x={x} y={y} width={w} height="1.4" fill="var(--st-hi)" />
          <rect x={x + w * 0.3} y={y + h * 0.4} width="3" height="4" fill="var(--st-dp)" opacity="0.4" />
        </g>
      ))}
      {/* blue domes (accent) */}
      {[[48, 66], [104, 66]].map(([cx, by]) => (
        <g key={cx}>
          {dome(cx, by, 8, 11, true)}
          <rect x={cx - 0.5} y={by - 20} width="1" height="6" fill="var(--gd-dp)" />
          <rect x={cx - 1.6} y={by - 18} width="3.2" height="1" fill="var(--gd-dp)" />
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Hagia Sophia — Istanbul
// ---------------------------------------------------------------------------
export function hagiaSophia(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="62" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="20" y="96" width="120" height="8" rx="2" fill="var(--st)" />
      {/* minarets */}
      {[24, 136].map((x) => (
        <g key={x}>
          <rect x={x - 2} y="34" width="4" height="62" fill="var(--st)" />
          <rect x={x - 2} y="34" width="1" height="62" fill="var(--st-hi)" />
          <rect x={x - 3} y="52" width="6" height="2" fill="var(--st-sh)" />
          {cone(x, 34, 6, 9)}
          <circle cx={x} cy="24" r="1.2" fill="var(--gd)" />
        </g>
      ))}
      {/* main mass */}
      <rect x="34" y="62" width="92" height="34" fill="var(--st)" />
      <rect x="34" y="62" width="92" height="1.6" fill="var(--st-hi)" />
      {/* semi-domes */}
      {dome(52, 62, 16, 16)}
      {dome(108, 62, 16, 16)}
      {/* central dome */}
      <rect x="64" y="50" width="32" height="12" fill="var(--st)" />
      {dome(80, 50, 20, 22)}
      <circle cx="80" cy="30" r="2.4" fill="var(--gd)" />
      <path d="M80 28 q2 -1 2 -3" fill="none" stroke="var(--gd)" strokeWidth="1" />
      {/* windows at dome base */}
      <g fill="var(--st-dp)" opacity="0.4">
        {[68, 74, 80, 86, 92].map((x) => (
          <rect key={x} x={x - 1} y="52" width="2" height="6" />
        ))}
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// The Little Mermaid — Copenhagen
// ---------------------------------------------------------------------------
export function littleMermaid(): ReactElement {
  return (
    <>
      {/* water */}
      <rect x="6" y="92" width="148" height="16" rx="3" fill="var(--gd)" opacity="0.28" />
      <path d="M6 96 Q40 92 80 96 Q120 100 154 96" fill="none" stroke="var(--gd)" strokeWidth="1.2" opacity="0.5" />
      {/* rock */}
      <path d="M52 96 Q56 78 74 76 Q94 74 100 90 L104 96 Z" fill="var(--st)" />
      <path d="M52 96 Q56 78 74 76 Q80 76 82 80 L74 96 Z" fill="var(--st-hi)" opacity="0.25" />
      <path d="M84 80 Q98 82 100 90 L104 96 L88 96 Z" fill="var(--st-dp)" opacity="0.35" />
      {/* seated mermaid (accent hue) */}
      <g fill="var(--gd)">
        <circle cx="78" cy="46" r="4.5" />
        <path d="M74 50 Q72 60 76 68 Q82 70 84 62 L82 52 Q80 49 78 50 Z" />
        {/* arm */}
        <path d="M82 54 Q88 56 90 64" fill="none" stroke="var(--gd)" strokeWidth="2.4" strokeLinecap="round" />
        {/* tail */}
        <path d="M76 66 Q70 76 60 78 Q68 72 70 66 Z" />
        <path d="M60 78 Q58 74 62 72 Q64 76 60 78 Z" />
      </g>
      <path d="M76 42 Q82 40 82 46" fill="none" stroke="var(--gd-dp)" strokeWidth="0.8" opacity="0.6" />
    </>
  );
}

// ---------------------------------------------------------------------------
// Nyhavn — Copenhagen
// ---------------------------------------------------------------------------
export function nyhavn(): ReactElement {
  const houses: [number, number][] = [[20, 44], [34, 58], [48, 50], [62, 64], [76, 46], [90, 60], [104, 52], [118, 62]];
  return (
    <>
      {/* canal */}
      <rect x="6" y="98" width="148" height="10" rx="3" fill="var(--gd)" opacity="0.28" />
      <path d="M6 101 Q40 98 80 101 Q120 104 154 101" fill="none" stroke="var(--gd)" strokeWidth="1" opacity="0.5" />
      {houses.map(([x, h], i) => {
        const y = 98 - h;
        const gable = i % 2 === 0;
        return (
          <g key={x}>
            <rect x={x} y={y} width="14" height={h} fill="var(--st)" />
            <rect x={x} y={y} width="1.3" height={h} fill="var(--st-hi)" />
            {gable
              ? <path d={`M${x} ${y} L${x + 7} ${y - 6} L${x + 14} ${y} Z`} fill="var(--st)" />
              : <rect x={x - 1} y={y - 3} width="16" height="3" fill="var(--st)" />}
            {[y + 8, y + 20, y + 32].map((wy) => (
              wy < 94 ? (
                <g key={wy}>
                  <rect x={x + 3} y={wy} width="3.5" height="5" fill="var(--st-dp)" opacity="0.4" />
                  <rect x={x + 8} y={wy} width="3.5" height="5" fill="var(--st-dp)" opacity="0.4" />
                </g>
              ) : null
            ))}
          </g>
        );
      })}
      {/* boat mast */}
      <rect x="134" y="52" width="1.6" height="46" fill="var(--st-dp)" />
      <path d="M135 56 L146 66 L135 70 Z" fill="var(--gd)" opacity="0.7" />
    </>
  );
}

// ---------------------------------------------------------------------------
// The Hermitage (Winter Palace) — St. Petersburg
// ---------------------------------------------------------------------------
export function hermitage(): ReactElement {
  return (
    <>
      <ellipse cx="80" cy="114" rx="66" ry="5" fill="var(--st-dp)" opacity="0.2" />
      <rect x="8" y="100" width="144" height="6" rx="2" fill="var(--st)" />
      <rect x="12" y="58" width="136" height="42" fill="var(--st)" />
      <rect x="12" y="58" width="136" height="1.6" fill="var(--st-hi)" />
      {/* central projecting bay */}
      <rect x="62" y="52" width="36" height="48" fill="var(--st)" />
      <rect x="62" y="52" width="1.6" height="48" fill="var(--st-hi)" />
      <path d="M60 52 L80 42 L100 52 Z" fill="var(--st)" />
      <path d="M60 52 L80 42 L80 52 Z" fill="var(--st-hi)" opacity="0.3" />
      <path d={archPath(72, 78, 16, 22)} fill="var(--st-dp)" opacity="0.5" />
      {/* column-and-window rhythm */}
      {[18, 28, 38, 48, 102, 112, 122, 132, 142].map((x) => (
        <g key={x}>
          <rect x={x} y="64" width="2.4" height="26" fill="var(--st-sh)" opacity="0.5" />
          <rect x={x + 4} y="66" width="4" height="9" fill="var(--st-dp)" opacity="0.38" />
          <rect x={x + 4} y="80" width="4" height="8" fill="var(--st-dp)" opacity="0.38" />
        </g>
      ))}
      {/* roofline statues + finial */}
      {[20, 40, 120, 140].map((x) => (
        <rect key={x} x={x - 1} y="53" width="2.4" height="5" rx="1" fill="var(--gd)" />
      ))}
      <circle cx="80" cy="46" r="1.8" fill="var(--gd)" />
    </>
  );
}
